import { useEffect, useState } from 'react'
import { ChevronDown, CheckCircle2, Download, ExternalLink, Share, Smartphone } from 'lucide-react'
import Sheet from './Sheet'
import { HELP_CATEGORIES } from '../../../components/site/faq.js'
import { canPromptInstall, isIOS, isStandalone, promptInstall, subscribeInstall } from '../install'

// The same answers as the website's /help page (shared data module), bundled
// into the register so staff can read them with no connection at all.
const SECTIONS = HELP_CATEGORIES.filter((category) => ['offline-register', 'troubleshooting'].includes(category.id))

export default function HelpSheet({ open, onClose }) {
  const [openId, setOpenId] = useState(null)
  const [installable, setInstallable] = useState(canPromptInstall())
  const [standalone, setStandalone] = useState(false)
  const [ios, setIos] = useState(false)

  useEffect(() => {
    setStandalone(isStandalone())
    setIos(isIOS())
    return subscribeInstall(() => setInstallable(canPromptInstall()))
  }, [])

  return (
    <Sheet open={open} onClose={onClose} title="Help" subtitle="Works offline · Offline register" maxHeight="92%">
      <section aria-labelledby="install-title" className="mb-5 rounded-2xl bg-brand-800 text-white p-4">
        <div className="flex items-start gap-3">
          <span className="h-10 w-10 shrink-0 grid place-items-center rounded-xl bg-white/10 text-lime"><Smartphone size={19} /></span>
          <div className="min-w-0">
            <h3 id="install-title" className="text-[15px] font-semibold">Install on this device</h3>
            {standalone ? (
              <p className="mt-1 flex items-center gap-1.5 text-sm text-[#b8cac2]"><CheckCircle2 size={15} className="text-lime" /> Installed. You are using the app.</p>
            ) : installable ? (
              <>
                <p className="mt-1 text-sm leading-relaxed text-[#b8cac2]">Add the register to your home screen so it opens full-screen, like any other app.</p>
                <button type="button" onClick={promptInstall} className="mt-3 inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-lime text-brand-800 text-sm font-semibold">
                  <Download size={16} /> Install app
                </button>
              </>
            ) : ios ? (
              <p className="mt-1 text-sm leading-relaxed text-[#b8cac2]">In Safari, tap <Share size={14} className="inline -mt-0.5" aria-label="Share" /> Share, then <b className="text-white font-semibold">Add to Home Screen</b>. It does not work inside WhatsApp or other in-app browsers.</p>
            ) : (
              <p className="mt-1 text-sm leading-relaxed text-[#b8cac2]">In Chrome, open the <b className="text-white font-semibold">⋮</b> menu and choose <b className="text-white font-semibold">Install app</b> or <b className="text-white font-semibold">Add to Home screen</b>.</p>
            )}
          </div>
        </div>
      </section>

      {SECTIONS.map((section) => (
        <section key={section.id} aria-labelledby={`help-${section.id}`} className="mb-5">
          <h3 id={`help-${section.id}`} className="text-xs font-semibold uppercase tracking-[0.1em] text-sage-500 mb-1 px-0.5">{section.title}</h3>
          <div className="divide-y divide-sage-200 border-y border-sage-200">
            {section.questions.map((question) => {
              const isOpen = openId === question.id
              return (
                <div key={question.id}>
                  <button
                    type="button"
                    onClick={() => setOpenId(isOpen ? null : question.id)}
                    aria-expanded={isOpen}
                    aria-controls={`answer-${question.id}`}
                    className="w-full min-h-14 flex items-center justify-between gap-3 py-3 text-left text-[15px] font-semibold text-sage-900"
                  >
                    <span>{question.q}</span>
                    <ChevronDown size={18} className={`shrink-0 text-sage-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </button>
                  <div id={`answer-${question.id}`} hidden={!isOpen} className="pc-help-answer pb-4 pr-6 text-sm leading-relaxed text-sage-700 select-text">
                    {question.a.map((block, index) => {
                      if (typeof block === 'string') return <p key={index}>{block}</p>
                      if (block.steps) return <ol key={index}>{block.steps.map((item) => <li key={item}>{item}</li>)}</ol>
                      return <ul key={index}>{block.list.map((item) => <li key={item}>{item}</li>)}</ul>
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      ))}

      <a
        href="/help"
        target="_blank"
        rel="noreferrer"
        className="mb-2 flex items-center justify-between gap-3 min-h-14 px-4 rounded-2xl bg-sage-50 ring-1 ring-sage-200 text-sm font-semibold text-brand-700"
      >
        <span>Full help centre <span className="font-normal text-sage-600">(needs internet)</span></span>
        <ExternalLink size={16} />
      </a>
    </Sheet>
  )
}
